import { api } from '../services/axiosConfig';
import { FittingDetail } from './fittings';

export interface PermissionGrant {
  id: number;
  subject_type: 'character' | 'corp' | 'alliance';
  subject_id: number;
  subject_name: string | null;
  role: 'admin' | 'member' | 'viewer' | 'director';
  created_by_character_id: number | null;
  created_at: string;
  /** Only ever set for a character-scoped 'director' grant — see
   * routes/admin/acl.ts's withEsiVerification. Null for every other row. */
  esi_director_verified_at: string | null;
}

export const fetchGrants = () => api.get<PermissionGrant[]>('/api/admin/acl/grants').then((r) => r.data);

export const createGrant = (body: {
  subjectType: 'character' | 'corp' | 'alliance';
  subjectId: number;
  subjectName?: string;
  role: 'admin' | 'member' | 'viewer' | 'director';
}) => api.post<PermissionGrant>('/api/admin/acl/grants', body).then((r) => r.data);

export const deleteGrant = (id: number) => api.delete(`/api/admin/acl/grants/${id}`);

export const resolveCharacterName = (name: string) =>
  api.post<{ id: number; name: string }>('/api/admin/characters/resolve', { name }).then((r) => r.data);

export const resolveOrgName = (name: string) =>
  api
    .post<{ id: number; name: string; type: 'corporation' | 'alliance' }>('/api/admin/corps/resolve', { name })
    .then((r) => r.data);

export interface CorpMember {
  id: number;
  name: string;
  /** ESI's real in-game roles, not this app's grants. Null when the roles
   * call itself failed (see `isRoleCheckAvailable`) — distinct from `false`,
   * which means ESI answered and this member genuinely has no Director role. */
  isEsiDirector: boolean | null;
}

export interface CorpMembersResponse {
  /** False when the roles half of the ESI call failed — the member list
   * above is still real, but isEsiDirector is null for everyone and the UI
   * should say "unavailable" rather than imply nobody is a director. */
  isRoleCheckAvailable: boolean;
  members: CorpMember[];
}

export const fetchCorpMembers = (corpId: number) =>
  api.get<CorpMembersResponse>(`/api/admin/corps/${corpId}/members`).then((r) => r.data);

export const startAdminReauth = () => api.post<{ auth_url: string }>('/api/auth/admin-reauth').then((r) => r.data);

export interface StaticCandidate {
  solar_system_id: number;
  system_name: string;
  wh_type_code: string;
  observations: number;
  distinct_observers: number;
  last_observed_at: string;
}

export const fetchStaticCandidates = () =>
  api.get<StaticCandidate[]>('/api/admin/statics/candidates').then((r) => r.data);

export const promoteStatic = (solarSystemId: number, whTypeCode: string) =>
  api.post('/api/admin/statics/promote', { solarSystemId, whTypeCode });

/** Admin-only login lockdown plus the settings the background workers read
 * on each tick — see src/services/appSettings.ts. */
export interface AdminSettings {
  adminOnlyLogin: boolean;
  trackingEnabled: boolean;
  trackingPollSeconds: number;
  webhooksEnabled: boolean;
  pollSecondsMin: number;
  pollSecondsMax: number;
  eveScoutStatus: { connections: number; lastUpdated: string | null };
}

export const fetchAdminSettings = () => api.get<AdminSettings>('/api/admin/settings').then((r) => r.data);

export const updateAdminSettings = (patch: Partial<Omit<AdminSettings, 'pollSecondsMin' | 'pollSecondsMax'>>) =>
  api.patch<AdminSettings>('/api/admin/settings', patch).then((r) => r.data);

export interface AdminUserRow {
  accountId: number;
  eveCharacterId: number;
  name: string;
  corpName: string | null;
  allianceName: string | null;
  lastLoginAt: string | null;
  isMain: boolean;
  isAdmin: boolean;
}

/** A bounded page plus the full count, so a caller can always tell how many
 * rows it is not being shown rather than silently rendering a truncated list. */
export interface Paged<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export const fetchAllUsers = (params?: { limit?: number; offset?: number }) =>
  api.get<Paged<AdminUserRow>>('/api/admin/users', { params }).then((r) => r.data);

export interface AdminFittingRow {
  id: number;
  name: string;
  ship_name: string;
  account_id: number;
}

export interface AdminFittingTag {
  id: number;
  fitting_id: number;
  /** Open-ended — 'wh_class' and 'site' are just the two built-in starting
   * categories, not an exhaustive list. See openUpFittingTagTypes in
   * src/db/index.ts. */
  tag_type: string;
  tag_value: string;
  recommendation: 'minimum' | 'recommended';
  /** False while a member-proposed tag is still waiting on endorsements —
   * see fitting_tag_endorsements (migration 014). */
  approved: boolean;
  created_by_character_id: number;
  created_by_name: string | null;
}

export const fetchAllFittings = (params?: { limit?: number; offset?: number }) =>
  api.get<Paged<AdminFittingRow>>('/api/admin/fittings-all', { params }).then((r) => r.data);

/** The full fit for whatever the "Tag a Fitting" picker has selected —
 * bypasses the normal view-scope check (see the route's own comment), since
 * the picker already lists fittings an admin couldn't otherwise open. */
export const fetchAdminFittingDetail = (id: number) =>
  api.get<FittingDetail>(`/api/admin/fittings/${id}`).then((r) => r.data);

export const fetchAllFittingTags = () => api.get<AdminFittingTag[]>('/api/admin/fitting-tags').then((r) => r.data);

/** Every distinct tag_type already in use, so the create form can offer
 * them as quick picks instead of every admin retyping "budget" slightly
 * differently. */
export const fetchFittingTagTypes = () => api.get<string[]>('/api/admin/fitting-tag-types').then((r) => r.data);

export const createFittingTag = (body: {
  fittingId: number;
  tagType: string;
  tagValue: string;
  recommendation: 'minimum' | 'recommended';
}) => api.post<{ id: number }>('/api/admin/fitting-tags', body).then((r) => r.data);

export const deleteFittingTag = (id: number) => api.delete(`/api/admin/fitting-tags/${id}`);

export interface VaultDeletionLogRow {
  id: number;
  vault_name: string;
  owner_character_id: number;
  owner_name: string | null;
  deleted_by_character_id: number;
  deleted_by_name: string | null;
  deleted_by_admin_override: 0 | 1;
  deleted_at: string;
}

export const fetchVaultDeletions = () =>
  api.get<VaultDeletionLogRow[]>('/api/admin/vault-deletions').then((r) => r.data);

// ── Corp operations ───────────────────────────────────────────────────────
//
// See routes/admin/audit.ts: aggregate contribution and tracking coverage.
// Deliberately carries no movement history — that would turn an opt-in
// convenience into surveillance of members' alts.

export interface OpsContributor {
  eve_character_id: number;
  name: string;
  signatures: number;
  connections: number;
  sightings: number;
  total: number;
}

export interface OpsCoverageRow {
  eveCharacterId: number;
  name: string;
  /** False when the character was linked without location scope, so tracking
   * silently never covers them. */
  canTrackLocation: boolean;
  authFailing: boolean;
  refreshAttempts: number;
  lastLoginAt: string | null;
}

export interface OpsResponse {
  windowDays: number;
  contributors: OpsContributor[];
  coverage: OpsCoverageRow[];
  blindSpots: number;
}

export const fetchOps = (days?: number) =>
  api.get<OpsResponse>('/api/admin/ops', { params: days ? { days } : undefined }).then((r) => r.data);
